// src/pages/Provider/CarCatalogProvider.jsx
import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_DOMAIN } from "config";
import { Heading, Img } from "../../components";
import CarCard from "../../components/CarCard";
import FilterBar from "components/FilterBar";
import CarsTable from "components/CarsTable";
import useDisplayMode from "useDisplayMode";
import useInfiniteScroll from "components/UseInfiniteScroll";

const tabs = [
  "מודעות בארכיון",
  "כל המודעות",
  "מודעות מאושרות",
  "מודעות עם הצעת מחיר",
  "מודעות שממתינות להצעה",
];

const TAB_FILTERS = {
  "כל המודעות": {},
  "מודעות שממתינות להצעה": { noOffersOnly: 1 },
  "מודעות עם הצעת מחיר": { pendingOnly: 1 },
  "מודעות מאושרות": { approvedOtherOnly: 1 },
  "מודעות בארכיון": { archiveOnly: 1 },
};

export default function CarCatalogProvider({ currentUser, setGlobalCounters }) {
  const [activeTab, setActiveTab] = useState("מודעות שממתינות להצעה");
  const [displayMode, setDisplayMode] = useDisplayMode();

  // ✅ הוספה קטנה: כמו בדילר
  const handleDisplayMode = (mode) => {
    setDisplayMode(mode);
    localStorage.setItem("displayMode", mode);
  };

  const [filters, setFilters] = useState({});
  const [cars, setCars] = useState([]);

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const PAGE_SIZE = 8;

  // ✅ מונע "הבהובים" עקב תשובות מאחרות
  const reqIdRef = useRef(0);

  // ✅ Lazy Load: מביא עמוד, ומוסיף/מאפס לפי reset
  const fetchCars = useCallback(
    async (pageToLoad = 1, reset = false) => {
      if (!currentUser?.id) return;

      const myReqId = ++reqIdRef.current;
      setLoading(true);

      const params = {
        ...filters,
        userId: currentUser.id,
        ownerOnly: 1, // ספק = רק רכבים שלו
        ...TAB_FILTERS[activeTab],

        // ✅ Lazy
        page: pageToLoad,
        pageSize: PAGE_SIZE,
      };

      try {
        const { data } = await axios.get(`${API_DOMAIN}/car/filterProviderCars`, { params });

        // אם בינתיים יצאה בקשה חדשה יותר – מתעלמים מהישנה
        if (myReqId !== reqIdRef.current) return;

        const list = Array.isArray(data) ? data : [];

        setCars((prev) => (reset ? list : [...prev, ...list]));
        setHasMore(list.length === PAGE_SIZE);
      } catch (err) {
        if (myReqId !== reqIdRef.current) return;
        console.error("שגיאת שליפת רכבים (Provider):", err);
        if (reset) setCars([]);
        setHasMore(false);
      } finally {
        if (myReqId === reqIdRef.current) setLoading(false);
      }
    },
    [currentUser?.id, filters, activeTab]
  );

  // ✅ sentinel: רק אם לא טוען ויש עוד
  const sentinelRef = useInfiniteScroll(() => {
    if (loading || !hasMore) return;
    setPage((p) => p + 1);
  });

  // ✅ RESET מלא כשמשנים פילטר/טאב
  useEffect(() => {
    if (!currentUser?.id) return;

    setPage(1);
    setHasMore(true);
    fetchCars(1, true);
  }, [filters, activeTab, currentUser?.id, fetchCars]);

  // ✅ APPEND כשעמוד עולה (Lazy)
  useEffect(() => {
    if (!currentUser?.id) return;
    if (page === 1) return;
    fetchCars(page, false);
  }, [page, currentUser?.id, fetchCars]);

  // ✅ Counters (לא נוגעים ב-cars כאן!)
  useEffect(() => {
    if (!currentUser?.id) return;

    const baseParams = {
      userId: currentUser.id,
      ownerOnly: 1,
      // בלי page/pageSize כדי לקבל הכל למונים
    };

    axios
      .get(`${API_DOMAIN}/car/filterProviderCars`, { params: baseParams })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setGlobalCounters({
          all: list.length,
          noOffers: list.filter((c) => Number(c.offer_status) === 0 && Number(c.is_archive) !== 1).length,
          pending: list.filter((c) => Number(c.offer_status) === 1).length,
          approved: list.filter((c) => Number(c.offer_status) === 2).length,
          archive: list.filter((c) => Number(c.is_archive) === 1).length,
        });
      })
      .catch((err) => {
        console.error("שגיאת מונים (Provider):", err);
        setGlobalCounters({ all: 0, noOffers: 0, pending: 0, approved: 0, archive: 0 });
      });
  }, [currentUser?.id, setGlobalCounters]);

  const providerLabel = currentUser?.provider_type === "private" ? "מוכר פרטי" : "ספק";

  return (
    <>
      <div className="car-catalog-dealer flex bg-blue_gray-50 p-8 sm:p-4" style={{ paddingTop: "1rem" }}>
        <div className="w-[100%] items-start gap-4 md:flex-col" style={{ gap: "2%" }}>
          <div className="flex relative flex-1 flex-col gap-8 self-center rounded-[24px] bg-secondary-primary_white p-6 md:self-stretch sm:p-4">
            <header className="flex flex-col items-start gap-3">
              <div className="absolute top-0 left-0 h-8 w-[20%] bg-[rgb(239,240,241)] flex items-center justify-center gap-4 rounded-br-[10px] md:px-5">
                <div className="flex w-full justify-center gap-1">
                  <Heading size="headinglg" as="h2" className="text-[12px] font-bold !text-gray-900_04">
                    <span className="text-gray-900_04">&nbsp;</span>
                    <span className="font-heebo font-medium text-gray-600">רכבים</span>
                  </Heading>
                  <Img src="images/img_signal_red_a700_03_16x16.svg" alt="Signal" className="h-[16px]" />
                </div>
                <div className="flex w-full items-center justify-center">
                  <Heading size="textxs" as="h3" className="!font-heebo text-[12px] font-medium !text-gray-600">
                    {providerLabel}
                  </Heading>
                  <Img src="images/img_vector_deep_orange_500.svg" alt="Image" className="h-[16px]" />
                </div>
              </div>

              <div className="flex items-center justify-between gap-5 mt-5 self-stretch border-b border-solid border-gray-200 md:flex-col">
                
                {/* ✅ תיקון 1: אייקונים כמו בדילר (כרטיסים/שורות) */}
                <div className="flex w-[6%] justify-center gap-3.5 md:w-full hide-mobile">
                  <Img
                    src="images/img_button1_gray_100_01.svg"
                    alt="Buttonone"
                    onClick={() => handleDisplayMode("cards")}
                    className="h-[32px] rounded-[10px] cursor-pointer"
                  />
                  <div className="flex items-center rounded-[10px] bg-gray-100_03 px-1.5 py-2.5">
                    <Img
                      src="images/img_megaphone_gray_600.svg"
                      onClick={() => handleDisplayMode("lines")}
                      alt="Megaphone"
                      className="h-[12px] cursor-pointer"
                    />
                  </div>
                </div>

                {/* ✅ תיקון 2: טאבים באמצע */}
    <div className="flex justify-center gap-4 xs-flex-row-reverse md:flex-row">
                  {tabs.map((tab) => (
                    <div
                      key={tab}
                      style={{marginRight: '0.4%',marginLeft: '0.5%', textAlign: 'center'}}
                      onClick={() => setActiveTab(tab)}
                      className={`flex py-3 cursor-pointer ${
                        activeTab === tab ? "border-b-2 border-red-a700_03" : ""
                      }`}
                    >
                      <Heading
                        as="h6"
                        className={`text-[16px] font-semibold t ${
                          activeTab === tab ? "text-black" : "!text-gray-600"
                        }`}
                      >
                        {tab}
                      </Heading>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3">
                  <Heading size="heading9xl" as="h4" className="!font-heebo text-[25px] font-bold lg:text-[21px]">
                    מודעות רכבים שלי
                  </Heading>
                  <Img src="images/img_car_red_a700_03_1.svg" alt="Car" className="h-[36px]" />
                </div>
              </div>
            </header>

            <div className="flex flex-col gap-6">
              <FilterBar onChange={setFilters} />

              {displayMode === "cards" && (
                <div className="grid grid-cols-4 justify-center gap-5 gap-y-5 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1">
                  <Suspense fallback={<div>Loading feed...</div>}>
                    {cars.map((car, index) => (
                      <CarCard key={`${car.id}-${index}`} currentUser={currentUser} {...car} />
                    ))}
                    <div ref={sentinelRef} style={{ height: 1 }} />
                  </Suspense>
                </div>
              )}

              {displayMode === "lines" && (
                <>
                  <CarsTable cars={cars} currentUser={currentUser} />
                  <div ref={sentinelRef} style={{ height: 1 }} />
                </>
              )}

              {loading && <div style={{ padding: 8 }}>
                ...טוען
                </div>}
              {!hasMore && cars.length > 0 && <div style={{ padding: 8 }}>אין עוד רכבים לטעינה</div>}
              {!loading && cars.length === 0 && <div style={{ padding: 8 }}>לא נמצאו רכבים</div>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
